import React, { useState, useEffect, useRef } from "react"
import { createPortal } from "react-dom"

// ─── Font size pairs [headingPx, questionPx] — index matches FONT_SIZE_STEPS ─
// Three steps: Small / Medium / Large
export const OVERLAY_FONT_PX = [
  { label: "Small", heading: 20, question: 18 },
  { label: "Medium", heading: 22, question: 20 },
  { label: "Large", heading: 24, question: 22 },
]

// ─── Responsive font size ─────────────────────────────────────────────────────
// Full size on a 1920-wide / 900-tall viewport and above; shrinks with the
// narrower or shorter side (e.g. laptops with 125–150% Windows scaling) but
// never below px * minRatio.
export const responsiveFontSize = (px, minRatio = 0.8) =>
  `clamp(${Math.round(px * minRatio)}px, min(${(px / 19.2).toFixed(3)}vw, ${(
    px / 9
  ).toFixed(3)}vh), ${px}px)`

export const OVERLAY_FONT_WEIGHTS = [
  { label: "Normal", value: 400 },
  { label: "Semibold", value: 600 },
  { label: "Bold", value: 700 },
]
export const OVERLAY_WEIGHT_DEFAULT = 0 // Normal

// ─── Key Stage presets ────────────────────────────────────────────────────────
// Each preset sets the recommended font, heading size, and question size.
// initialFontStep index refers to OVERLAY_FONT_PX above.
//
//  KS1 (Yr 1–2)   → Sassoon Primary,  heading 24px (Large=2),  question 22px
//  KS2 (Yr 3–6)   → Montserrat,       heading 22px (Medium=1), question 20px
//  KS3/4 (Sec.)   → Open Sans,        heading 20px (Small=0),  question 18px
export const KS_PRESETS = {
  KS1: {
    label: "KS1 (Years 1-2)",
    initialFontStep: 2,
    initialFontFamily:
      '"Sassoon Primary", "Comic Sans MS", "Trebuchet MS", sans-serif',
  },
  KS2: {
    label: "KS2 (Years 3-6)",
    initialFontStep: 1,
    initialFontFamily: '"Montserrat", sans-serif',
  },
  KS3: {
    label: "KS3 & KS4 (Secondary)",
    initialFontStep: 0,
    initialFontFamily: '"Open Sans", sans-serif',
  },
}

// ─── Available fonts ──────────────────────────────────────────────────────────
export const OVERLAY_FONTS = [
  { label: "Open Sans", value: '"Open Sans", sans-serif' },
  {
    label: "Sassoon Primary",
    value: '"Sassoon Primary", "Comic Sans MS", "Trebuchet MS", sans-serif',
  },
  { label: "Montserrat", value: '"Montserrat", sans-serif' },
  { label: "Quicksand", value: '"Quicksand", sans-serif' },
  { label: "Roboto", value: '"Roboto", sans-serif' },
  {
    label: "Century Gothic",
    value: '"Century Gothic", "Apple Gothic", "URW Gothic", sans-serif',
  },
  // { label: "Apertura", value: '"Apertura", "Open Sans", sans-serif' },
  {
    label: "Apertura",
    value: '"Apertura", "Poppins", "Futura", sans-serif',
  },
]

// ─── Colour overlay options ───────────────────────────────────────────────────
export const OVERLAY_COLORS = {
  none: { label: "None", value: "transparent", swatch: "#ffffff" },
  cream: { label: "Cream", value: "#FBF3D5", swatch: "#FBF3D5" },
  mint: { label: "Mint", value: "#D7EFE0", swatch: "#D7EFE0" },
  blue: { label: "Blue", value: "#D6EAF8", swatch: "#D6EAF8" },
  lavender: { label: "Lavender", value: "#E6E0F8", swatch: "#E6E0F8" },
  pink: { label: "Pink", value: "#F8DDE5", swatch: "#F8DDE5" },
}

export const KEY_STAGE_FONTS = {
  0: '"Sassoon Primary", "Comic Sans MS", "Trebuchet MS", sans-serif', // KS1
  1: '"Open Sans", sans-serif',                                        // KS2
  2: '"Montserrat", sans-serif',                                       // KS3 & KS4
};

export const OVERLAY_FONT_DEFAULT = 1 // Medium index into OVERLAY_FONT_PX
export const OVERLAY_INTENSITY_DEFAULT = 40

// ─── Storage helpers ──────────────────────────────────────────────────────────
function readInt(key, fallback) {
  const v = parseInt(localStorage.getItem(key), 10)
  return isNaN(v) ? fallback : v
}
function readStr(key, fallback) {
  return localStorage.getItem(key) || fallback
}

// ─── Colour blending ──────────────────────────────────────────────────────────
// The overlay is applied by mixing the chosen swatch directly into a surface's
// own `background-color` (page + question card), NOT by painting a separate
// translucent layer over the page. A layer stacked over everything has no way
// to tell "this pixel is a letter" from "this pixel is a background" — it can
// only ever look at colour values, so it inevitably shifts wording along with
// whatever is behind it. Baking the blend into background-color only changes
// that CSS property; a piece of text's own `color` is a completely separate
// property this never touches, so wording stays exactly as authored. The
// answer-option rows (.hw-list ul li / .hw-list ul a) are deliberately left
// out of this — they keep their plain white background and never tint.
function hexToRgb(hex) {
  let h = hex.replace("#", "")
  if (h.length === 3) h = h.split("").map(c => c + c).join("")
  const num = parseInt(h, 16)
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 }
}
function rgbToHex({ r, g, b }) {
  const toHex = v => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`
}
/** Mix `tintHex` into `baseHex` at `pct` (0-100). pct 0 = baseHex, pct 100 = tintHex. */
function blendHex(baseHex, tintHex, pct) {
  const a = Math.max(0, Math.min(100, pct)) / 100
  const base = hexToRgb(baseHex)
  const tint = hexToRgb(tintHex)
  return rgbToHex({
    r: base.r * (1 - a) + tint.r * a,
    g: base.g * (1 - a) + tint.g * a,
    b: base.b * (1 - a) + tint.b * a,
  })
}
// Original colours of the two surfaces the overlay tints — kept in sync with
// index.css's `body` and `.question_container` / `.nv-sticky-header` rules,
// which read the blended result back via var(--ov-page-bg) / var(--ov-surface-bg).
const PAGE_BASE_BG = "#f2f8fd"
const SURFACE_BASE_BG = "#ffffff"

export function getKeyStage(level) {
    const lowerLevel = level?.toLowerCase();

    if(!lowerLevel) {
      return 2;
    }

    // Check Primary
    const primaryMatch = lowerLevel.match(/primary\s*(\d+)/);
    if (primaryMatch) {
        const year = parseInt(primaryMatch[1], 10);
        if (year <= 2) return 0;
        if (year <= 6) return 1;
    }

    // Check Year
    const yearMatch = lowerLevel.match(/year\s*(\d+)/);
    if (yearMatch) {
        const year = parseInt(yearMatch[1], 10);
        if (year <= 2) return 0;
        if (year <= 6) return 1;
        if (year <= 13) return 2;
    }

    // Check Secondary
    if (lowerLevel.includes("secondary")) {
        return 2;
    }

    return 2;
}


export function getFontFamily(level) {
  const keyStage = getKeyStage(level);
  return KEY_STAGE_FONTS[keyStage];
}

// ─── Hook: useOverlayOptions ──────────────────────────────────────────────────
//
// Options (all optional):
//
//   storageKey        – localStorage key prefix, keeps each module's settings
//                       separate.  Default: "ov"
//                       e.g. "comprehension" → comprehension_color, …
//
//   initialFontStep   – 0 (Small) | 1 (Medium) | 2 (Large)
//                       Sets BOTH heading and question sizes via OVERLAY_FONT_PX.
//                       Only applied when no saved value exists yet.
//                       Default: 1 (Medium)
//
//   initialFontFamily – any OVERLAY_FONTS[n].value
//                       Only applied when no saved value exists yet.
//                       Default: OVERLAY_FONTS[0].value (Open Sans)
//
// Quickstart with a Key Stage preset:
//   import { useOverlayOptions, KS_PRESETS } from "./OverlayOptions"
//
//   // KS1
//   const options = useOverlayOptions({ storageKey: "comprehension", ...KS_PRESETS.KS1 })
//   // KS2
//   const options = useOverlayOptions({ storageKey: "comprehension", ...KS_PRESETS.KS2 })
//   // KS3/4
//   const options = useOverlayOptions({ storageKey: "comprehension", ...KS_PRESETS.KS3 })
//
// Returned values:
//   cardStyle    – apply to the question-card wrapper  (question font size + family)
//   headingStyle – apply to headings / passage titles  (heading font size  + family)

export function useOverlayOptions({
  storageKey = "ov",
  initialFontStep = OVERLAY_FONT_DEFAULT,
  initialFontFamily = OVERLAY_FONTS[0].value,
   initialFontWeight = OVERLAY_WEIGHT_DEFAULT,
} = {}) {
  // Per-module localStorage keys
  const KEY_COLOR = `${storageKey}_color`
  const KEY_INTENSITY = `${storageKey}_intensity`
  const KEY_FONTSTEP = `${storageKey}_fontstep`
  const KEY_FONTFAMILY = `${storageKey}_fontfamily`
  const KEY_FONTWEIGHT = `${storageKey}_fontweight`
  const clampStep = v => Math.min(OVERLAY_FONT_PX.length - 1, Math.max(0, v))
  const clampWeight = v => Math.min(OVERLAY_FONT_WEIGHTS.length - 1, Math.max(0, v))

  const [overlayColor, setOverlayColor] = useState(() =>
    readStr(KEY_COLOR, "none")
  )
  const [overlayIntensity, setOverlayIntensity] = useState(() =>
    readInt(KEY_INTENSITY, OVERLAY_INTENSITY_DEFAULT)
  )
  const [overlayFontStep, setOverlayFontStep] = useState(() =>
    clampStep(readInt(KEY_FONTSTEP, initialFontStep))
  )
  const [overlayFontFamily, setOverlayFontFamily] = useState(() =>
    readStr(KEY_FONTFAMILY, initialFontFamily)
  )
const [overlayFontWeight, setOverlayFontWeight] = useState(() =>
  clampWeight(readInt(KEY_FONTWEIGHT, initialFontWeight))
)
  // Helper — save to localStorage then fire the custom event so any component
  // using useOverlayStyle() re-renders immediately without needing Context.
  function saveAndNotify(key, value) {
    localStorage.setItem(key, value)
    window.dispatchEvent(new Event("overlaychange"))
  }

  // Persist on change + notify all listeners
  useEffect(() => {
    saveAndNotify(KEY_COLOR, overlayColor)
  }, [overlayColor])
  useEffect(() => {
    saveAndNotify(KEY_INTENSITY, String(overlayIntensity))
  }, [overlayIntensity])
  useEffect(() => {
    saveAndNotify(KEY_FONTSTEP, String(overlayFontStep))
  }, [overlayFontStep])
  useEffect(() => {
    saveAndNotify(KEY_FONTFAMILY, overlayFontFamily)
  }, [overlayFontFamily])
useEffect(() => {
  saveAndNotify(KEY_FONTWEIGHT, String(overlayFontWeight))
}, [overlayFontWeight])

  // ── Page-level background tint ────────────────────────────────────────────
  // Blends the chosen colour straight into <body>'s own background-color (via
  // the --ov-page-bg custom property body's rule reads in index.css), instead
  // of stacking a translucent layer above the page. Text has its own separate
  // `color` property that background-color changes never touch, so no amount
  // of intensity can shift the wording — only the page's background shade.
  //
  // --ov-surface-bg is ALSO set here, on <body>, in addition to being set
  // locally on the question-card wrapper via cardStyle below. Custom
  // properties resolve to the nearest ancestor that sets them, so this body-
  // level value is only what elements OUTSIDE the question card fall back to
  // (the question-number tabs above the card, for instance) — anything
  // inside the card still gets cardStyle's own (identical) value. Without
  // this, only descendants of the card would ever see the variable at all.
  useEffect(() => {
    if (typeof document === "undefined") return
    if (overlayColor === "none") {
      document.body.style.removeProperty("--ov-page-bg")
      document.body.style.removeProperty("--ov-surface-bg")
    } else {
      const active = OVERLAY_COLORS[overlayColor] || OVERLAY_COLORS.none
      document.body.style.setProperty(
        "--ov-page-bg",
        blendHex(PAGE_BASE_BG, active.value, overlayIntensity)
      )
      document.body.style.setProperty(
        "--ov-surface-bg",
        blendHex(SURFACE_BASE_BG, active.value, overlayIntensity)
      )
    }
  }, [overlayColor, overlayIntensity])
  // Clean up on unmount so this hook never leaves a stray override behind on
  // a shared <body> after the component using it goes away.
  useEffect(() => {
    return () => {
      if (typeof document !== "undefined") {
        document.body.style.removeProperty("--ov-page-bg")
        document.body.style.removeProperty("--ov-surface-bg")
      }
    }
  }, [])

  // Reset → this module's initial defaults
  const resetDefaults = () => {
    setOverlayColor("none")
    setOverlayIntensity(OVERLAY_INTENSITY_DEFAULT)
    setOverlayFontStep(clampStep(initialFontStep))
    setOverlayFontFamily(initialFontFamily)
    setOverlayFontWeight(clampWeight(initialFontWeight))
  }

  const sizes = OVERLAY_FONT_PX[overlayFontStep]
  const activeColor = OVERLAY_COLORS[overlayColor] || OVERLAY_COLORS.none

  /** Apply to each question-card wrapper */
  const cardStyle = {
    position: "relative",
    fontSize: responsiveFontSize(sizes.question),
    fontWeight: OVERLAY_FONT_WEIGHTS[overlayFontWeight].value,
    fontFamily: overlayFontFamily,
    // Custom properties inherit down the DOM by default, so this reaches
    // .question_container / .nv-sticky-header (descendants of whatever this
    // style is applied to) without those elements needing any style of their
    // own — their CSS rules just read var(--ov-surface-bg, #ffffff). The
    // answer-option rows deliberately do NOT read this var, so they stay
    // plain white regardless. Omitted entirely when the overlay is off, so
    // the var() falls back to the original white and nothing changes.
    ...(overlayColor !== "none" && {
      "--ov-surface-bg": blendHex(SURFACE_BASE_BG, activeColor.value, overlayIntensity),
    }),
  }

  /** Apply to headings / passage titles inside each question card */
  const headingStyle = {
    fontSize: responsiveFontSize(sizes.heading),
    fontFamily: overlayFontFamily,
    fontWeight: OVERLAY_FONT_WEIGHTS[overlayFontWeight].value,
  }

  return {
    overlayColor,
    setOverlayColor,
    overlayIntensity,
    setOverlayIntensity,
    overlayFontStep,
    setOverlayFontStep,
    overlayFontFamily,
    setOverlayFontFamily,
    resetDefaults,
    cardStyle,
    headingStyle,
    // expose current px values for consumers that need raw numbers
    currentHeadingPx: sizes.heading,
    overlayFontWeight,
setOverlayFontWeight,
    currentQuestionPx: sizes.question,
  }
}

// ─── CSS ──────────────────────────────────────────────────────────────────────
// Montserrat/Roboto aren't loaded with a Semibold (600) face anywhere else in the
// app (Roboto's global import only pulls 400/500), so requesting fontWeight:600
// silently falls back to the nearest loaded weight — making it render identical
// to Bold (700). Pull in the weights this component actually offers.
const OVERLAY_CSS = `
  @import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&family=Roboto:wght@400;600;700&display=swap");
  .mg-ov-toggle{display:inline-flex;align-items:center;justify-content:center;gap:10px;padding:12px 22px;background:linear-gradient(180deg,#fff,#f8fafc);border:1px solid #D1D9E6;border-radius:999px;cursor:pointer;font-size:16px;font-weight:700;color:#1E293B;line-height:1;white-space:nowrap;box-shadow:0 4px 12px rgba(15,23,42,.08);transition:all .25s ease;user-select:none;}
  .mg-ov-toggle:hover{background:#fff;border-color:#3B82F6;color:#2563EB;box-shadow:0 8px 20px rgba(59,130,246,.18);transform:translateY(-1px);}
  .mg-ov-toggle:active{transform:translateY(0) scale(.98);box-shadow:0 3px 8px rgba(15,23,42,.12);}
.mg-ov-panel{position:fixed;top:16px;right:16px;z-index:10001;width:500px;max-width:96%;background:#ffffff;border:1px solid #ECEFF4;border-radius:16px;box-shadow:0 12px 40px rgba(15,23,42,0.18);padding:0;box-sizing:border-box;font-size:14px;color:#1F2A44;display:flex;flex-direction:column;overflow:hidden;}  .mg-ov-title{font-size:18px;font-weight:700;}
  .mg-ov-close{background:none;border:none;cursor:pointer;font-size:22px;line-height:1;color:#64748B;}
.mg-ov-label{font-size:11px;font-weight:700;letter-spacing:0.4px;color:#475569;margin-bottom:6px;}.mg-ov-swatches{display:grid;grid-template-columns:repeat(6,1fr);gap:8px;margin-bottom:10px;}  .mg-ov-swatch-cell{text-align:center;}
  .mg-ov-head{display:flex;align-items:center;justify-content:space-between;padding:14px 18px 10px;border-bottom:1px solid #F1F5F9;}
  .mg-ov-swatch{position:relative;width:100%;height:46px;border-radius:12px;cursor:pointer;border:1px solid #E2E8F0;}
  .mg-ov-swatch.is-selected{border:2px solid #22C55E;box-shadow:0 0 0 2px rgba(34,197,94,0.15);}
  .mg-ov-swatch--none{background:#ffffff;}
  .mg-ov-swatch--cream{background:#FBF3D5;}
  .mg-ov-swatch--mint{background:#D7EFE0;}
  .mg-ov-swatch--blue{background:#D6EAF8;}
  .mg-ov-swatch--lavender{background:#E6E0F8;}
  .mg-ov-swatch--pink{background:#F8DDE5;}
  .mg-ov-check{position:absolute;top:-7px;right:-7px;width:18px;height:18px;background:#22C55E;border-radius:50%;color:#fff;font-size:11px;line-height:18px;text-align:center;font-weight:700;}
  .mg-ov-swatch-label{font-size:12px;color:#475569;margin-top:4px;}
.mg-ov-row{display:flex;align-items:center;gap:8px;margin-bottom:10px;}  .mg-ov-row--between{justify-content:space-between;}
  .mg-ov-step{width:34px;height:34px;border-radius:50%;border:1px solid #E2E8F0;background:#fff;cursor:pointer;font-size:18px;color:#1F2A44;}
  .mg-ov-step:disabled{opacity:0.4;cursor:not-allowed;}
  .mg-ov-as{display:flex;align-items:center;gap:10px;}
  .mg-ov-a{line-height:1;font-weight:700;color:#1F2A44;border:2px solid transparent;border-radius:8px;height:35px;width:35px;align-items:center;justify-content:center;display:flex;cursor:pointer;}
  .mg-ov-a--sm{font-size:14px;}
  .mg-ov-a--md{font-size:18px;}
  .mg-ov-a--lg{font-size:24px;}
  .mg-ov-a.is-current{border-color:#EF4444;}
  .mg-ov-size-preview{display:flex;gap:16px;margin-bottom:6px;padding:10px 12px;background:#F8FAFC;border-radius:10px;border:1px solid #E2E8F0;}
  .mg-ov-size-preview-item{display:flex;flex-direction:column;gap:2px;}
  .mg-ov-size-preview-lbl{font-size:10px;font-weight:600;color:#94A3B8;letter-spacing:0.4px;text-transform:uppercase;}
  .mg-ov-size-preview-val{font-size:13px;font-weight:700;color:#1E293B;}
  .mg-ov-slider{flex:1;accent-color:#EF4444;cursor:pointer;}
  .mg-ov-pct{min-width:52px;text-align:center;border:1px solid #E2E8F0;border-radius:8px;padding:6px 4px;font-weight:600;font-size:13px;}
  .mg-ov-reset{width: fit-content;margin-left:auto; max-width: 100%;display:inline-flex;align-items:center;justify-content:center;gap:8px;background:#fff;border:1px solid #FCA5A5;color:#EF4444;border-radius:999px;padding:8px 16px;cursor:pointer;font-weight:800;font-size:18px;}
 .mg-ov-divider{border:none;border-top:1px solid #F1F5F9;margin:2px 0 10px;}
  .mg-ov-body{flex:1;overflow-y:auto;overscroll-behavior:contain;padding:12px 18px 4px;}
  .mg-ov-body::-webkit-scrollbar{width:3px;}
  .mg-ov-body::-webkit-scrollbar-track{background:transparent;}
  .mg-ov-body::-webkit-scrollbar-thumb{background:#CBD5E1;border-radius:999px;}
  .mg-ov-body::-webkit-scrollbar-thumb:hover{background:#94A3B8;}
  .mg-ov-foot{ display:flex; padding:10px 18px 14px;border-top:1px solid #F1F5F9;}
  .mg-ov-font-row{display:flex;gap:16px;align-items:stretch;margin-bottom:10px;}
  .mg-ov-font-col{flex:1;min-width:0;}
  .mg-ov-font-sep{width:1px;background:#E2E8F0;align-self:stretch;margin-top:20px;}
  .mg-ov-dark{background:#111116 !important;border-color:#2e2e45 !important;color:#f1f5f9 !important;}
.mg-ov-dark .mg-ov-head{background:#111116 !important;border-bottom-color:#2e2e45 !important;}
.mg-ov-dark .mg-ov-title{color:#f1f5f9 !important;}
.mg-ov-dark .mg-ov-close{color:#94a3b8 !important;}
.mg-ov-dark .mg-ov-label{color:#94a3b8 !important;}
.mg-ov-dark .mg-ov-body{background:#111116 !important;}
.mg-ov-dark .mg-ov-step{background:#1a1a26 !important;border-color:#2e2e45 !important;color:#f1f5f9 !important;}
.mg-ov-dark .mg-ov-a{color:#94a3b8 !important;}
.mg-ov-dark .mg-ov-a.is-current{color:#3b82f6 !important;}
.mg-ov-dark .mg-ov-divider{border-color:#2e2e45 !important;}
.mg-ov-dark .mg-ov-swatch{border-color:#2e2e45 !important;}
.mg-ov-dark .mg-ov-swatch-label{color:#94a3b8 !important;}
.mg-ov-dark .mg-ov-family-select select{background:#1a1a26 !important;border-color:#2e2e45 !important;color:#f1f5f9 !important;}
.mg-ov-dark .mg-ov-intensity-track{background:#2e2e45 !important;}
.mg-ov-dark .mg-ov-reset{background:#1a1a26 !important;border-color:#2e2e45 !important;color:#f1f5f9 !important;}
.mg-ov-dark .mg-ov-fw-btn{background:#1a1a26 !important;border-color:#2e2e45 !important;color:#94a3b8 !important;}
.mg-ov-dark .mg-ov-fw-btn.is-active{background:#3b82f6 !important;border-color:#3b82f6 !important;color:#fff !important;} 
.mg-ov-dark .mg-ov-foot{background:#111116 !important;border-top-color:#2e2e45 !important;}
.mg-ov-dark .mg-ov-pct{background:#1a1a26 !important;border-color:#2e2e45 !important;color:#f1f5f9 !important;}
.mg-ov-dark .mg-ov-font-sep{background:#2e2e45 !important;}
.mg-ov-dark .mg-ov-size-preview{background:#1a1a26 !important;border-color:#2e2e45 !important;}
.mg-ov-dark .mg-ov-size-preview-lbl{color:#64748b !important;}
.mg-ov-dark .mg-ov-size-preview-val{color:#f1f5f9 !important;}
.mg-ov-dark .form-select{background:#1a1a26 !important;border-color:#2e2e45 !important;color:#f1f5f9 !important;}
.mg-ov-dark .mg-ov-slider{accent-color:#3b82f6 !important;}
.mg-ov-dark .mg-ov-body::-webkit-scrollbar-thumb{background:#2e2e45 !important;}
.mg-ov-dark .mg-ov-swatch--none{background:#1a1a26 !important;}
`
// ─── OverlayToggleButton ──────────────────────────────────────────────────────
export function OverlayToggleButton({ onClick }) {
  return (
    <>
      <style>{OVERLAY_CSS}</style>
      <button type="button" className="mg-ov-toggle" onClick={onClick}>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#5B6BF5"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="13.5" cy="6.5" r="1.5" />
          <circle cx="17.5" cy="10.5" r="1.5" />
          <circle cx="8.5" cy="7.5" r="1.5" />
          <circle cx="6.5" cy="12.5" r="1.5" />
          <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125 0-.937.75-1.687 1.688-1.687h1.999C18.358 15.25 22 13.92 22 9.5 22 5.36 17.5 2 12 2z" />
        </svg>
        <span className="fw-semibold text-center">Overlay Options</span>
      </button>
    </>
  )
}

// ─── OverlayPanel ─────────────────────────────────────────────────────────────
export function OverlayPanel({
  overlayColor,
  setOverlayColor,
  overlayIntensity,
  setOverlayIntensity,
  overlayFontStep,
  setOverlayFontStep,
  overlayFontFamily,
  isDarkMode,
  setOverlayFontFamily,
  overlayFontWeight,
setOverlayFontWeight,
  onClose,
  onReset,
  panelRef,
  style,
}) {
  const sizes = OVERLAY_FONT_PX[overlayFontStep]

  return (
    <div className={`mg-ov-panel${isDarkMode ? " mg-ov-dark" : ""}`} ref={panelRef} style={style}>
      {/* ── Header ── */}
      <div className="mg-ov-head">
        <span className="mg-ov-title">Overlay Options</span>
        <button
          type="button"
          className="mg-ov-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="mg-ov-body">
        {/* ── Colour overlay ── */}
        <div className="mg-ov-label">COLOUR OVERLAY</div>
        <div className="mg-ov-swatches">
          {Object.keys(OVERLAY_COLORS).map(key => {
            const c = OVERLAY_COLORS[key]
            const selected = overlayColor === key
            return (
              <div key={key} className="mg-ov-swatch-cell">
                <button
                  type="button"
                  className={`mg-ov-swatch mg-ov-swatch--${key}${
                    selected ? " is-selected" : ""
                  }`}
                  onClick={() => setOverlayColor(key)}
                >
                  {selected && <span className="mg-ov-check">✓</span>}
                </button>
                <div className="mg-ov-swatch-label">{c.label}</div>
              </div>
            )
          })}
        </div>

        <hr className="mg-ov-divider" />

        <div className="mg-ov-font-row">
          {/* ── Font size ── */}
          <div className="mg-ov-font-col">
            <div className="mg-ov-label">FONT SIZE</div>
            <div className="mg-ov-row mg-ov-row--between">
              <button
                type="button"
                className="mg-ov-step"
                onClick={() => setOverlayFontStep(s => Math.max(0, s - 1))}
                disabled={overlayFontStep <= 0}
              >
                −
              </button>
              <div className="mg-ov-as">
                {OVERLAY_FONT_PX.map((pair, i) => (
                  <span
                    key={pair.label}
                    className={`mg-ov-a mg-ov-a--${
                      { 0: "sm", 1: "md", 2: "lg" }[i]
                    }${overlayFontStep === i ? " is-current" : ""}`}
                    onClick={() => setOverlayFontStep(i)}
                    title={`Heading ${pair.heading}px / Question ${pair.question}px`}
                  >
                    A
                  </span>
                ))}
              </div>
              <button
                type="button"
                className="mg-ov-step"
                onClick={() =>
                  setOverlayFontStep(s =>
                    Math.min(OVERLAY_FONT_PX.length - 1, s + 1)
                  )
                }
                disabled={overlayFontStep >= OVERLAY_FONT_PX.length - 1}
              >
                +
              </button>
            </div>
          </div>

          <div className="mg-ov-font-sep" aria-hidden="true" />

          {/* ── Font weight ── */}
          <div className="mg-ov-font-col">
            <div className="mg-ov-label">FONT WEIGHT</div>
            <div className="mg-ov-row mg-ov-row--between">
              <button
                type="button"
                className="mg-ov-step"
                onClick={() => setOverlayFontWeight(w => Math.max(0, w - 1))}
                disabled={overlayFontWeight <= 0}
              >
                −
              </button>
              <div className="mg-ov-as">
                {OVERLAY_FONT_WEIGHTS.map((w, i) => (
                  <span
                    key={w.label}
                    className={`mg-ov-a${overlayFontWeight === i ? " is-current" : ""}`}
                    style={{ fontWeight: w.value }}
                    onClick={() => setOverlayFontWeight(i)}
                    title={w.label}
                  >
                    B
                  </span>
                ))}
              </div>
              <button
                type="button"
                className="mg-ov-step"
                onClick={() =>
                  setOverlayFontWeight(w => Math.min(OVERLAY_FONT_WEIGHTS.length - 1, w + 1))
                }
                disabled={overlayFontWeight >= OVERLAY_FONT_WEIGHTS.length - 1}
              >
                +
              </button>
            </div>
          </div>
        </div>

        <hr className="mg-ov-divider" />

        <div className="mg-ov-label">FONT FAMILY</div>
        <select
          className="form-select"
          value={overlayFontFamily}
          onChange={e => setOverlayFontFamily(e.target.value)}
        >
          {OVERLAY_FONTS.map(font => (
            <option key={font.label} value={font.value}>
              {font.label}
            </option>
          ))}
        </select>
        <hr className="mg-ov-divider" />
        {/* ── Overlay intensity ── */}
        <div className="mg-ov-label">OVERLAY INTENSITY</div>
        <div className="mg-ov-row">
          <button
            type="button"
            className="mg-ov-step"
            onClick={() => setOverlayIntensity(v => Math.max(0, v - 10))}
          >
            −
          </button>

          <input
            type="range"
            min="0"
            max="100"
            step="1"
            className="mg-ov-slider"
            value={overlayIntensity}
            onChange={e => setOverlayIntensity(parseInt(e.target.value, 10))}
          />

          <button
            type="button"
            className="mg-ov-step"
            onClick={() => setOverlayIntensity(v => Math.min(100, v + 10))}
          >
            +
          </button>

          <span className="mg-ov-pct">{overlayIntensity}%</span>
        </div>
      </div>
      {/* end mg-ov-body */}

      <div className="mg-ov-foot">
        {/* ── Reset ── */}
        <button type="button" className="mg-ov-reset" onClick={onReset}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#EF4444"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
            <path d="M3 3v5h5" />
          </svg>
          Reset to Default
        </button>
      </div>
      {/* end mg-ov-foot */}
    </div>
  )
}

// ─── OverlayUI ────────────────────────────────────────────────────────────────
// Tint layer + panel together. Drop inside any component that calls the hook.
export function OverlayUI({
  overlayColor,
  setOverlayColor,
  overlayIntensity,
  setOverlayIntensity,
  overlayFontStep,
  setOverlayFontStep,
  overlayFontFamily,
  setOverlayFontFamily,
  overlayFontWeight,
  setOverlayFontWeight,
  panelOpen,
  setPanelOpen,
  onReset,
  panelPos,
   isDarkMode,
}) {
  // NOTE: there is deliberately no rendered "tint layer" here any more. The
  // colour itself is applied by useOverlayOptions, which blends it straight
  // into <body>'s and the question-card's own background-color (see
  // PAGE_BASE_BG / SURFACE_BASE_BG / --ov-page-bg / --ov-surface-bg above).
  // An overlay div stacked on top of the page can only ever look at colour
  // values, so it inevitably shifts wording along with the backgrounds behind
  // it. Changing background-color directly cannot do that: a piece of text's
  // own `color` is a separate property that background-color changes never
  // touch — and the answer-option rows simply don't read the var, so they're
  // never affected at all.
  const panelRef = useRef(null)
  useEffect(() => {
    if (!panelOpen) return
    function handleDown(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setPanelOpen(false)
      }
    }
    document.addEventListener("mousedown", handleDown)
    return () => document.removeEventListener("mousedown", handleDown)
  }, [panelOpen])
  return (
    <>
      <style>{OVERLAY_CSS}</style>

      {panelOpen &&
        createPortal(
          <OverlayPanel
            overlayColor={overlayColor}
            setOverlayColor={setOverlayColor}
            overlayIntensity={overlayIntensity}
            setOverlayIntensity={setOverlayIntensity}
            overlayFontStep={overlayFontStep}
            setOverlayFontStep={setOverlayFontStep}
        overlayFontWeight={overlayFontWeight}
setOverlayFontWeight={setOverlayFontWeight}
            overlayFontFamily={overlayFontFamily}
            setOverlayFontFamily={setOverlayFontFamily}
            onClose={() => setPanelOpen(false)}
           isDarkMode={isDarkMode}
            panelRef={panelRef}
            onReset={onReset}
            style={
              panelPos
                ? {
                    top: panelPos.top,
                    right: Math.max(
                      8,
                      Math.min(panelPos.right, window.innerWidth - 500 - 8)
                    ),
                    maxHeight: panelPos.maxHeight,
                  }
                : undefined
            }
          />,
          document.body
        )}
    </>
  )
}

// ─── Default export: fully self-contained ─────────────────────────────────────
export default function OverlayOptions({
  storageKey,
  initialFontStep,
  initialFontFamily,
   initialFontWeight,
} = {}) {
  const [panelOpen, setPanelOpen] = useState(false)
  const options = useOverlayOptions({
    storageKey,
    initialFontStep,
    initialFontFamily,
    initialFontWeight,
  })

  return (
    <OverlayUI
      {...options}
      panelOpen={panelOpen}
      setPanelOpen={setPanelOpen}
      onReset={options.resetDefaults}
    />
  )
}